import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  isJa?: boolean;
};

/**
 * 関西空港から日本語の島へ降り立つプロローグ・シネマティックモーダル
 * - 雲海を抜けて島が現れるシネマティックアニメーション
 */
export function PrologueVideoModal({ isOpen, onClose, isJa = true }: Props) {
  const [step, setStep] = useState(0);

  const stories = isJa
    ? [
        {
          title: "海をこえて、日本語の島へ",
          text: "飛行機が雲を抜けると、光る海と緑の島が見えてきた。",
          icon: "✈️",
          bg: "from-sky-400 via-sky-200 to-amber-100",
        },
        {
          title: "関西空港に到着",
          text: "連絡橋を渡ると、どうぶつたちが暮らす新しい日常が始まる。",
          icon: "🛬",
          bg: "from-blue-500 via-indigo-300 to-pink-100",
        },
        {
          title: "言葉を覚えて、島を歩こう",
          text: "一つ日本語を覚えるたびに、霧が晴れて新しい道が開くよ！",
          icon: "🏝️",
          bg: "from-emerald-400 via-teal-200 to-amber-50",
        },
      ]
    : [
        {
          title: "Across the Sea, to Nihongo Island",
          text: "Passing through the clouds, a shimmering island appears across the ocean.",
          icon: "✈️",
          bg: "from-sky-400 via-sky-200 to-amber-100",
        },
        {
          title: "Arriving at Kansai Airport",
          text: "Crossing the bridge begins your cozy life alongside friendly animal residents.",
          icon: "🛬",
          bg: "from-blue-500 via-indigo-300 to-pink-100",
        },
        {
          title: "Learn Words, Expand the Island",
          text: "Every phrase you learn clears the fog and unlocks brand new paths!",
          icon: "🏝️",
          bg: "from-emerald-400 via-teal-200 to-amber-50",
        },
      ];

  if (!isOpen) return null;

  const current = stories[step];
  const isLast = step === stories.length - 1;

  function handleNext() {
    if (isLast) {
      onClose();
      setStep(0);
    } else {
      setStep((s) => s + 1);
    }
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className={`relative w-full max-w-lg rounded-3xl p-6 sm:p-8 shadow-2xl text-center overflow-hidden bg-gradient-to-b ${current.bg}`}
        >
          {/* 雲の流れる演出 */}
          <motion.div
            animate={{ x: [-80, 80, -80], opacity: [0.5, 0.8, 0.5] }}
            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
            className="absolute -top-6 left-1/4 text-6xl text-white/50 pointer-events-none"
          >
            ☁️
          </motion.div>

          {/* アイコンアニメーション */}
          <motion.div
            key={step}
            initial={{ scale: 0.6, y: -20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 14 }}
            className="relative z-10 w-24 h-24 mx-auto mb-4 rounded-full bg-white/80 shadow-lg flex items-center justify-center text-5xl border-2 border-white"
          >
            <motion.span
              animate={{ y: [-3, 3, -3], rotate: [-4, 4, -4] }}
              transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            >
              {current.icon}
            </motion.span>
          </motion.div>

          {/* タイトル */}
          <h2 className="relative z-10 text-xl sm:text-2xl font-black text-[#273f7a] mb-2">
            {current.title}
          </h2>

          {/* 説明文 */}
          <p className="relative z-10 text-xs sm:text-sm text-stone-700 font-bold leading-relaxed max-w-sm mx-auto mb-6">
            {current.text}
          </p>

          {/* ステップインジケーター */}
          <div className="relative z-10 flex justify-center gap-2 mb-6">
            {stories.map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === step ? "w-6 bg-[#8f332c]" : "w-2 bg-stone-400/50"
                }`}
              />
            ))}
          </div>

          {/* ボタン */}
          <div className="relative z-10 flex items-center justify-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl font-bold text-xs text-stone-600 hover:text-stone-900 bg-white/50 hover:bg-white/70 transition"
            >
              {isJa ? "スキップ" : "Skip"}
            </button>
            <button
              onClick={handleNext}
              className="px-6 py-2.5 rounded-xl font-black text-sm text-white bg-[#8f332c] hover:bg-[#a63c34] shadow-md hover:shadow-lg transition transform active:scale-95"
            >
              {isLast ? (isJa ? "島へ行こう！🐾" : "Enter Island! 🐾") : (isJa ? "次へ" : "Next")}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
